import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, Typography } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import html2canvas from 'html2canvas';

interface ScreenshotCaptureProps {
  onCapture: (screenshot: string) => void;
  onClose: () => void;
}

const ScreenshotCapture: React.FC<ScreenshotCaptureProps> = ({ onCapture, onClose }) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [endPoint, setEndPoint] = useState({ x: 0, y: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsSelecting(true);
    setStartPoint({ x: e.clientX, y: e.clientY });
    setEndPoint({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isSelecting) return;
    setEndPoint({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    if (!isSelecting) return;
    setIsSelecting(false);
  };

  const handleCapture = async () => {
    if (!overlayRef.current) return;

    const rect = {
      left: Math.min(startPoint.x, endPoint.x),
      top: Math.min(startPoint.y, endPoint.y),
      width: Math.abs(endPoint.x - startPoint.x),
      height: Math.abs(endPoint.y - startPoint.y),
    };

    if (rect.width < 10 || rect.height < 10) {
      alert('Please select a larger area');
      return;
    }

    // Hide overlay before capturing
    overlayRef.current.style.display = 'none';

    try {
      const canvas = await html2canvas(document.body, {
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY,
        width: rect.width,
        height: rect.height,
        allowTaint: true,
        useCORS: true,
      });

      const screenshot = canvas.toDataURL('image/png');
      onCapture(screenshot);
      // Don't restore overlay after successful capture
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      alert('Failed to capture screenshot: ' + (error as Error).message);
      // Only restore overlay on error
      if (overlayRef.current) {
        overlayRef.current.style.display = 'block';
      }
    }
  };

  const selectionRect = {
    left: Math.min(startPoint.x, endPoint.x),
    top: Math.min(startPoint.y, endPoint.y),
    width: Math.abs(endPoint.x - startPoint.x),
    height: Math.abs(endPoint.y - startPoint.y),
  };

  const content = (
    <>
      <Box
        ref={overlayRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 9999,
          cursor: 'crosshair',
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {/* Close button */}
        <Button
          color="secondary"
          onClick={onClose}
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            backgroundColor: 'white',
            color: 'black',
            zIndex: 10000,
            minWidth: 'auto',
            p: 1,
            '&:hover': { backgroundColor: '#f0f0f0' },
          }}
        >
          <CloseIcon />
        </Button>

        {/* Instructions */}
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'white',
            borderRadius: 2,
            p: 2,
            zIndex: 10000,
            boxShadow: 3,
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
            Select area to capture
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Click and drag to select the area you want to screenshot
          </Typography>
        </Box>

        {/* Selection rectangle */}
        {(selectionRect.width > 0 || selectionRect.height > 0) && (
          <Box
            sx={{
              position: 'fixed',
              left: selectionRect.left,
              top: selectionRect.top,
              width: selectionRect.width,
              height: selectionRect.height,
              border: '2px solid #1976d2',
              backgroundColor: 'rgba(25, 118, 210, 0.1)',
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>

      {/* Capture button - rendered outside overlay for better event handling */}
      {!isSelecting && selectionRect.width > 10 && selectionRect.height > 10 && (
        <Button
          variant="contained"
          color="success"
          startIcon={<CameraAltIcon />}
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            void handleCapture();
          }}
          sx={{
            position: 'fixed',
            left: selectionRect.left + selectionRect.width / 2,
            top: selectionRect.top + selectionRect.height + 10,
            transform: 'translateX(-50%)',
            zIndex: 10002,
          }}
        >
          Take screenshot
        </Button>
      )}
    </>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  // Portal to body so the overlay is not trapped under AppBar/Drawer stacking contexts.
  return createPortal(content, document.body);
};

export default ScreenshotCapture;
